import { ReactElement } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { Stack, Typography } from '@mui/material'
import {
  getGetSeismicAnalysisEnabledQueryKey,
  useGetSeismicAnalysisEnabled,
  useSetSeismicAnalysisEnabled,
} from '@api/seismic-analysis/seismic-analysis'
import { useQueryClient } from '@tanstack/react-query'
import { DisabledSection } from '@ui2/structure'
import { SwitchLabeled } from '@ui/actions'
import { enqueueSnackbar } from '@ui/snackbar'
import { Box } from '@ui/structure'
import { useConfigStore } from '@stores'
import { useResultsInvalidation } from '@editorHooks'
import { FormCollapsibleSection, FormSectionIcons } from '@editorComponents'
import { buildErrorMessage } from 'src/constants/errors'
import SimplifiedSeismicConfigForm from './components/SimplifiedSeismicConfigForm'
import SimplifiedSeismicModelInputsForm from './components/SimplifiedSeismicModelInputsForm'
import SimplifiedSeismicResults from './components/SimplifiedSeismicResults'

/**
 * The three stages of the simplified substitute-force procedure, each in its own
 * collapsible section: the configuration feeds the model data, which in turn
 * feeds the substitute forces.
 *
 * The switch on top drives the project's seismic analysis flag, which decides
 * whether the seismic loads enter the horizontal distribution at all. While it
 * is off the stages stay visible but inactive - the configuration is kept, so
 * switching back on restores the model that was calculated before.
 */
const SimplifiedSeismicSettings = (): ReactElement => {
  const { projectId }: { projectId?: string } = useParams()
  const appConfig = useConfigStore.getState()

  const queryClient = useQueryClient()
  const invalidateResultsQueries = useResultsInvalidation()

  const { data: analysisEnabled, isLoading: isLoadingEnabled } = useGetSeismicAnalysisEnabled(
    projectId as string,
    {
      query: {
        onError: (error: AxiosError) => {
          enqueueSnackbar(
            buildErrorMessage(error, 'Fehler beim Laden des Erdbebennachweis-Status'),
            { variant: 'error' },
          )
        },
      },
    },
  )

  const { mutate: setAnalysisEnabled, isLoading: isSavingEnabled } = useSetSeismicAnalysisEnabled({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries(getGetSeismicAnalysisEnabledQueryKey(projectId))
        invalidateResultsQueries(projectId as string)
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Umschalten des Erdbebennachweises'), {
          variant: 'error',
        })
      },
    },
  })

  if (!appConfig.ff_simplified_seismic) {
    return (
      <Box p={2} paddingTop={0} border={1} borderColor="grey.200" borderRadius={1}>
        <Typography>Vereinfachter Erdbebennachweis noch nicht verfügbar.</Typography>
      </Box>
    )
  }

  const isEnabled = analysisEnabled?.do_seismic_analysis ?? false
  // Only grey out once the flag is known to be off, so the sections do not
  // flash inactive while the query is still running.
  const isInactive = analysisEnabled?.do_seismic_analysis === false

  return (
    <Stack direction="column" spacing={1} data-cy="simplified-seismic-settings">
      <SwitchLabeled
        checked={isEnabled}
        disabled={isLoadingEnabled || isSavingEnabled}
        onChange={event =>
          setAnalysisEnabled({
            projectGuid: projectId as string,
            data: { do_seismic_analysis: event.target.checked },
          })
        }
        label="Erdbebennachweis aktiv"
        infoTooltip={{
          title:
            'Ist der Nachweis deaktiviert, gehen keine Erdbebenlasten in die Horizontallastverteilung ein. Die Konfiguration bleibt erhalten und wird beim Wiedereinschalten weiterverwendet.',
        }}
        data-cy="seismic-analysis-enabled-switch"
      />

      <DisabledSection disabled={isInactive}>
        <Stack direction="column" spacing={1}>
          <FormCollapsibleSection
            label="Erdbebenkonfiguration - vereinfachtes Verfahren"
            startIcon={FormSectionIcons.settings}
            defaultExpanded
            dataCy="simplified-seismic-config-section"
          >
            <SimplifiedSeismicConfigForm />
          </FormCollapsibleSection>

          <FormCollapsibleSection
            label="Modelldaten"
            startIcon={FormSectionIcons.geometry}
            dataCy="simplified-seismic-model-inputs-section"
          >
            <SimplifiedSeismicModelInputsForm />
          </FormCollapsibleSection>

          <FormCollapsibleSection
            label="Ersatzkräfte"
            startIcon={FormSectionIcons.stiffening}
            dataCy="simplified-seismic-results-section"
          >
            <SimplifiedSeismicResults />
          </FormCollapsibleSection>
        </Stack>
      </DisabledSection>
    </Stack>
  )
}

export default SimplifiedSeismicSettings
